import { PifIdentityMatch } from "./workflowRunData";
import { PIF_DETAILS_FIELDS, PIF_EMPLOYMENT_TYPES, PIF_LOOKUP_FLOWS } from "./pifWorkflowKeys";
import { isPifStartDatePast, isValidLicenseNumber } from "../../pages/supervision/PostInterviewForm/pifValidation";

/** pif-3's answers by field key (PIF_DETAILS_FIELDS). Dropdowns hold the option's ID. */
export type PifDetailsValues = Record<string, string>;

export interface PifOption {
  id: string;
  text: string;
}

/** What pif-3 picks from and decides by: the live PostIF reference data. */
export interface PifDetailsReference {
  departments: PifOption[];
  /** Positions of the selected department, already narrowed to the caller's Can Hire scope. */
  positions: PifOption[];
  reportsTo: PifOption[];
  rateTypes: PifOption[];
  services: PifOption[];
  /** Clients of the selected service. */
  clients: PifOption[];
  /** The selected position's hiring band; null until one is chosen, or when it has none. */
  band: { min: number; max: number } | null;
  /** The selected position may operate an agency vehicle: the driving questions apply. */
  driving: boolean;
  /** A Community DSP position needs a service and client, and is always part-time. */
  communityDsp: boolean;
  fullTimeAllowed: boolean;
  /** PostIF's "email required" setting. */
  emailRequired: boolean;
}

/** What every pif-3 section is given. `onChange` sets one key; the stage clears what depends on it. */
export interface PifSectionProps {
  identity: PifIdentityMatch;
  values: PifDetailsValues;
  /** Field errors by key: the screen's own rules, then the server's on submit. */
  errors: Record<string, string>;
  reference: PifDetailsReference;
  onChange: (fieldKey: string, value: string) => void;
}

const F = PIF_DETAILS_FIELDS;

export const PIF_DETAILS_KEYS: string[] = Object.values(F);

/** Boolean field values, as the engine stores them. */
export const PIF_BOOLEAN = { yes: "true", no: "false" } as const;

const HOURS_MIN = 0.5;
const HOURS_MAX = 60;
const NOTES_MAX = 1000;
const EMERGENCY_CONTACT_PATTERN = /^[0-9]{1,30}$/;

/** Who the lookup found decides what pif-3 collects and what it locks (decisions log section 2). */
export const pifDetailsMode = (flow: PifIdentityMatch["flow"]) => ({
  /** Existing employee: copied from the employee record. Rehire: carried over. Both read-only. */
  identityLocked: flow === PIF_LOOKUP_FLOWS.existingActive || flow === PIF_LOOKUP_FLOWS.rehire,
  /** An existing employee maintains their own contact details, so pif-3 doesn't collect them. */
  collectsContact: flow !== PIF_LOOKUP_FLOWS.existingActive,
  /** Only an existing employee chooses a position switch or an addition. */
  asksSwitchOrAddition: flow === PIF_LOOKUP_FLOWS.existingActive,
  /** "Known as" and "Suffix": a new hire only. An existing employee's or rehire's identity carries
   *  over locked, and a PIF only changes position and pay (P6, Pooja on 80430, 2026-10-08). */
  asksKnownNameAndSuffix: flow === PIF_LOOKUP_FLOWS.new,
});

/**
 * What pif-3 starts from for this identity, for any key the case hasn't saved yet: an existing
 * employee's name from the employee record, and a rehire's name plus their old contact details.
 * A new hire starts blank.
 */
export const pifIdentityDefaults = (identity: PifIdentityMatch): PifDetailsValues => {
  if (identity.flow === PIF_LOOKUP_FLOWS.new || identity.flow === PIF_LOOKUP_FLOWS.blocked) return {};
  const names = {
    [F.firstName]: identity.firstName ?? "",
    [F.middleName]: identity.middleName ?? "",
    [F.lastName]: identity.lastName ?? "",
  };
  if (identity.flow === PIF_LOOKUP_FLOWS.existingActive) return names;
  return {
    ...names,
    [F.address1]: identity.address1 ?? "",
    [F.city]: identity.city ?? "",
    [F.state]: identity.state ?? "",
    [F.zip]: identity.zip ?? "",
    [F.homePhone]: identity.homePhone ?? "",
    [F.cell]: identity.cell ?? "",
    [F.email]: identity.email ?? "",
  };
};

/**
 * What pif-3 writes when it first shows for this identity: the defaults for any key the case has no
 * value for yet, and an empty switch / addition unless the person is an existing employee (3.2: "not
 * shown and stored empty for new hire and rehire"). Null when there's nothing to write.
 */
export const missingPifDefaults = (values: PifDetailsValues, identity: PifIdentityMatch): PifDetailsValues | null => {
  const fill = Object.entries(pifIdentityDefaults(identity)).filter(([key, value]) => value && !values[key]);
  if (!pifDetailsMode(identity.flow).asksSwitchOrAddition && values[F.switchOrAddition]) {
    fill.push([F.switchOrAddition, ""]);
  }
  return fill.length ? Object.fromEntries(fill) : null;
};

/** Decision 3.3's wording, for an agency vehicle with no licence. */
export const PIF_VEHICLE_NEEDS_LICENSE = "An agency vehicle can only be driven with a driver's licence.";

/**
 * pif-3's screen rules, by field key. The server only marks a field required when every flow needs
 * it (80432), so the flow- and position-dependent rules live here (decisions log section 2, 3.1–3.3).
 */
export const validatePifDetails = (
  values: PifDetailsValues,
  identity: PifIdentityMatch,
  reference: PifDetailsReference,
): Record<string, string> => {
  const errors: Record<string, string> = {};
  const value = (key: string) => (values[key] ?? "").trim();
  const require = (key: string, message = "Required.") => {
    if (!value(key)) errors[key] = message;
  };
  const mode = pifDetailsMode(identity.flow);

  require(F.firstName);
  require(F.lastName);

  if (mode.collectsContact) {
    require(F.address1);
    require(F.city);
    require(F.zip);
    if (!value(F.homePhone) && !value(F.cell)) errors[F.cell] = "Enter a home phone or a cell.";
    if (reference.emailRequired) require(F.email);
  }

  if (mode.asksSwitchOrAddition) require(F.switchOrAddition, "Choose a switch or an addition.");
  require(F.department);
  require(F.position);
  require(F.reportsTo);
  require(F.firstWorkingDay);

  const hours = Number(value(F.hoursPerWeek));
  if (!value(F.hoursPerWeek)) errors[F.hoursPerWeek] = "Required.";
  else if (Number.isNaN(hours) || hours < HOURS_MIN || hours > HOURS_MAX)
    errors[F.hoursPerWeek] = `Between ${HOURS_MIN} and ${HOURS_MAX} hours.`;

  require(F.employmentType);
  if (value(F.employmentType) === PIF_EMPLOYMENT_TYPES.fullTime && !reference.fullTimeAllowed)
    errors[F.employmentType] = "This position is part-time only.";

  if (reference.communityDsp) {
    require(F.service);
    require(F.client);
  }

  require(F.rateType);
  // Flag, never block (3.1): out of band is fine; only a missing or non-numeric rate is an error.
  const rate = Number(value(F.rate));
  if (!value(F.rate)) errors[F.rate] = "Required.";
  else if (Number.isNaN(rate) || rate < 0) errors[F.rate] = "Enter the rate as a number.";

  if (value(F.approverNotes).length > NOTES_MAX) errors[F.approverNotes] = `${NOTES_MAX} characters at most.`;
  else if (isPifStartDatePast(value(F.firstWorkingDay)))
    require(F.approverNotes, "The first working day has passed: explain why for the approvers.");

  // Driving positions (3.3): both questions; a licence number when either is yes; and operating an
  // agency vehicle needs a licence.
  if (reference.driving) {
    require(F.hasLicense);
    require(F.willOperateVehicle);
    const holdsLicense = value(F.hasLicense) === PIF_BOOLEAN.yes;
    const operatesVehicle = value(F.willOperateVehicle) === PIF_BOOLEAN.yes;
    if (operatesVehicle && value(F.hasLicense) === PIF_BOOLEAN.no) errors[F.willOperateVehicle] = PIF_VEHICLE_NEEDS_LICENSE;
    if (holdsLicense || operatesVehicle) {
      if (!value(F.licenseNumber)) errors[F.licenseNumber] = "Enter the driver license number.";
      else if (!isValidLicenseNumber(value(F.licenseNumber))) errors[F.licenseNumber] = "Enter a valid NY license number.";
    }
  }

  if (!value(F.emergencyContactNumber)) errors[F.emergencyContactNumber] = "Required.";
  else if (!EMERGENCY_CONTACT_PATTERN.test(value(F.emergencyContactNumber)))
    errors[F.emergencyContactNumber] = "Digits only, up to 30.";

  return errors;
};

/**
 * The rate hint under the Rate field: flag-only against the band (3.1, AC6). A chosen position with
 * no band on file says so, rather than asking for a position that is already selected.
 */
export const pifRateMessage = (rateText: string, band: PifDetailsReference["band"], positionChosen = false): string => {
  if (!band)
    return positionChosen ? "No hiring band is set up for this position." : "Select a position to see the hiring band.";
  const range = `$${band.min}–$${band.max}`;
  const rate = Number(rateText);
  if (!rateText || Number.isNaN(rate)) return `Hiring band: ${range}/hr.`;
  return rate < band.min || rate > band.max
    ? `Rate $${rate.toFixed(2)} is outside the band ${range}. Directors will see a flag.`
    : `Rate $${rate.toFixed(2)} is within band ${range}.`;
};

/** The rate hint's colour: green inside the band, orange outside it, plain when there is nothing to compare. */
export const pifRateTone = (rateText: string, band: PifDetailsReference["band"]): "success" | "warning" | undefined => {
  const rate = Number(rateText);
  if (!band || !rateText || Number.isNaN(rate)) return undefined;
  return rate < band.min || rate > band.max ? "warning" : "success";
};

/**
 * What an approver signs off on (P6, Pooja on 80430, 2026-10-08): the person, the position and
 * department by name, the employment type, the first working day, and the rate. Every field is a
 * display string, "" when pif-3 hasn't answered it.
 */
export interface PifApplicantSummary {
  name: string;
  position: string;
  department: string;
  employmentType: string;
  firstWorkingDay: string;
  rate: string;
}

const EMPLOYMENT_TYPE_LABELS: Record<string, string> = {
  [PIF_EMPLOYMENT_TYPES.fullTime]: "Full-time",
  [PIF_EMPLOYMENT_TYPES.partTime]: "Part-time",
};

/** "2026-10-15" -> "10/15/2026", by string: a date-only value is never put through `Date`, which
 *  would shift it by the browser's offset. Anything else is shown as saved. */
export const toDisplayDay = (value: string | undefined): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? "");
  return match ? `${match[2]}/${match[3]}/${match[1]}` : (value ?? "");
};

/** Shown while the server has yet to name an ID the case saved — never the ID itself. */
export const PIF_NAME_PENDING = "Loading…";

/** A saved display name; "Loading…" while its ID is saved but the server hasn't named it yet. */
const savedName = (values: PifDetailsValues, nameKey: string, idKey: string): string =>
  values[nameKey] || (values[idKey] ? PIF_NAME_PENDING : "");

/**
 * The approval summary, from pif-3's answers. Department and position come from the names the
 * SERVER wrote from their IDs once pif-3 was submitted (pif.departmentName / pif.positionName), so an
 * approver needs no PostIF access, sees what was requested even after a rename, and never sees an ID
 * or text the requester's browser supplied (Pooja, 2026-10-08).
 */
export const toPifApplicantSummary = (values: PifDetailsValues): PifApplicantSummary => {
  const name = [values[F.firstName], values[F.middleName], values[F.lastName]]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(" ");
  const employmentType = values[F.employmentType] ?? "";
  return {
    name,
    position: savedName(values, F.positionName, F.position),
    department: savedName(values, F.departmentName, F.department),
    employmentType: EMPLOYMENT_TYPE_LABELS[employmentType] ?? employmentType,
    firstWorkingDay: toDisplayDay(values[F.firstWorkingDay]),
    rate: values[F.rate] ?? "",
  };
};
